import React, { useRef } from 'react';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import InfiniteLoader from 'react-window-infinite-loader';

import { DemandWidgetDTO, demandWidgetService } from 'entities/demand';
import { withSuspense } from 'shared/lib/react';
import { Loader } from 'shared/ui/Loader';
import { VirtualizedTableList } from 'shared/ui/VirtualTable';

import { columns, DEFAULT_SORTING, PAGE_SIZE, ROW_HEIGHT, THRESHOLD_VALUE } from './DemandWidget.const';
import { mapDataToTableColumns } from './DemandWidget.libs';

import DemandRowWrapper from './ui/demandRowWrapper';
import styles from './DemandWidget.module.scss';

const DemandWidget: React.FC = () => {
  const { t } = useTranslation();
  const loaderRef = useRef<InfiniteLoader>(null);

  const {
    data: demandWidgetData,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useSuspenseInfiniteQuery(demandWidgetService.queryOptions({ size: PAGE_SIZE, sort: DEFAULT_SORTING }));

  const mappedData = demandWidgetData?.pages?.reduce<DemandWidgetDTO[]>((acc, { content }) => [...acc, ...content], []);

  const tableDataMapped = mapDataToTableColumns(mappedData);

  return (
    <div className={styles.demandWidgetHolder}>
      <Typography.Title level={5}>{t('Demand:DemandWidgetList.heading')}</Typography.Title>
      <div className={styles.listWrapper} data-testid="demand-widget">
        <VirtualizedTableList
          columns={columns}
          dataSource={tableDataMapped}
          rowHeight={ROW_HEIGHT}
          loadNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isNextPageLoading={isFetching}
          loaderRef={loaderRef}
          threshold={THRESHOLD_VALUE}
          bordered={false}
          showHeader={false}
          rowWrapper={DemandRowWrapper}
        />
      </div>
    </div>
  );
};

export default withSuspense(DemandWidget, { fallback: <Loader /> });
